import { token } from '../../theme';
import { CanvasComponent } from '../canvas-base';

/**
 * Spectrum analyzer view. Bar-style FFT with colored bars near each FSK target frequency
 * and dashed marker lines + labels (F0..F3).
 */
export class Spectrum extends CanvasComponent {
  private analyser: AnalyserNode | null = null;
  private data: Uint8Array<ArrayBuffer> | null = null;
  private sampleRate = 48000;
  private maxFreq = 6000;

  freqs: number[] = [1200, 1600, 2000, 2400];

  private colSurface = '#0a0e16';
  private colBorder = '#1a2332';
  private colGridMinor = 'rgba(80,110,160,0.08)';
  private colGridMajor = 'rgba(80,140,200,0.16)';
  private colDim = '#7e8a9a';
  private colMute = '#3e4858';
  private traceColors: string[] = ['#00d9ff', '#a3ff00', '#ffb000', '#ff3a8c'];
  private noiseFill = 'rgba(120,160,210,0.18)';

  constructor() {
    super();
    this.el.classList.add('spectrum');
    this.attachLifecycle();
    this.cacheColors();
  }

  bind(analyser: AnalyserNode | null, sampleRate?: number): void {
    this.analyser = analyser;
    if (analyser) this.data = new Uint8Array(analyser.frequencyBinCount);
    if (sampleRate) this.sampleRate = sampleRate;
  }

  protected override onThemeChanged(): void {
    this.cacheColors();
  }

  private cacheColors(): void {
    this.colSurface = token('display-bg');
    this.colBorder = token('border-hairline');
    this.colGridMinor = token('graticule-minor');
    this.colGridMajor = token('graticule-major');
    this.colDim = token('text-dim');
    this.colMute = token('text-mute');
    this.traceColors = [
      token('trace-f0'),
      token('trace-f1'),
      token('trace-f2'),
      token('trace-f3'),
    ];
    this.noiseFill = token('graticule-major');
  }

  protected draw(): void {
    if (this.w === 0 || this.h === 0) return;
    const { ctx, w, h } = this;

    ctx.fillStyle = this.colSurface;
    ctx.fillRect(0, 0, w, h);

    this.drawGrid();
    this.drawAxisLabels();

    if (this.analyser && this.data) {
      this.analyser.getByteFrequencyData(this.data);
      this.drawBars();
    }

    this.drawMarkers();
    this.drawCornerLabels();
    this.drawBorder();
  }

  private drawGrid(): void {
    const { ctx, w, h } = this;
    const divsX = 10;
    const divsY = 6;

    ctx.lineWidth = 1;
    ctx.strokeStyle = this.colGridMinor;
    ctx.setLineDash([1, 3]);
    ctx.beginPath();
    for (let x = 1; x < divsX * 5; x++) {
      const px = (x * w) / (divsX * 5);
      ctx.moveTo(px, 0);
      ctx.lineTo(px, h);
    }
    for (let y = 1; y < divsY * 5; y++) {
      const py = (y * h) / (divsY * 5);
      ctx.moveTo(0, py);
      ctx.lineTo(w, py);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = this.colGridMajor;
    ctx.beginPath();
    for (let x = 0; x <= divsX; x++) {
      ctx.moveTo(Math.round((x * w) / divsX) + 0.5, 0);
      ctx.lineTo(Math.round((x * w) / divsX) + 0.5, h);
    }
    for (let y = 0; y <= divsY; y++) {
      ctx.moveTo(0, Math.round((y * h) / divsY) + 0.5);
      ctx.lineTo(w, Math.round((y * h) / divsY) + 0.5);
    }
    ctx.stroke();
  }

  private drawAxisLabels(): void {
    const { ctx, w, h, maxFreq } = this;
    ctx.font = `9px ${token('font-mono')}`;
    ctx.fillStyle = this.colMute;
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left';
    const stepKHz = 1;
    for (let f = 0; f <= maxFreq; f += stepKHz * 1000) {
      const x = (f / maxFreq) * w;
      const label = (f / 1000).toFixed(0) + 'k';
      if (f === 0) {
        ctx.fillText(label, x + 3, h - 3);
      } else {
        ctx.fillText(label, x + 3, h - 3);
      }
    }

    // dB axis (left)
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const dbLabels = ['0', '-20', '-40', '-60', '-80', '-100'];
    for (let i = 0; i < dbLabels.length; i++) {
      const y = (i / (dbLabels.length - 1)) * h;
      ctx.fillText(dbLabels[i] + ' dB', 4, Math.max(2, y + 2));
    }
  }

  private drawBars(): void {
    if (!this.data || !this.analyser) return;
    const { ctx, w, h } = this;
    const binCount = Math.floor(
      this.maxFreq / (this.sampleRate / this.analyser.fftSize),
    );
    const barW = w / binCount;

    for (let i = 0; i < binCount; i++) {
      const v = this.data[i] / 255;
      const barH = v * h;
      const f = (i * this.sampleRate) / this.analyser.fftSize;

      let color = this.noiseFill;
      for (let k = 0; k < this.freqs.length; k++) {
        if (Math.abs(f - this.freqs[k]) < 80) {
          color = this.traceColors[k] ?? color;
          break;
        }
      }
      ctx.fillStyle = color;
      ctx.fillRect(i * barW, h - barH, Math.max(1, barW - 1), barH);
    }
  }

  private drawMarkers(): void {
    const { ctx, w, h, maxFreq } = this;
    const narrow = w < 360;
    const compact = w < 480;
    const fontSize = narrow ? 9 : compact ? 9 : 10;
    ctx.font = `bold ${fontSize}px ${token('font-mono')}`;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.setLineDash([3, 4]);

    for (let i = 0; i < this.freqs.length; i++) {
      const x = (this.freqs[i] / maxFreq) * w;
      const c = this.traceColors[i];
      ctx.strokeStyle = c;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(Math.round(x) + 0.5, 0);
      ctx.lineTo(Math.round(x) + 0.5, h);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = c;

      const label = narrow
        ? `F${i}`
        : `F${i} ${(this.freqs[i] / 1000).toFixed(1)}k`;
      const tw = ctx.measureText(label).width;
      // Label position: stagger vertically per index, clamp X within canvas
      const ly = Math.min(h - fontSize - 4, 14 + i * (fontSize + 3));
      const lx = Math.max(2, Math.min(x + 4, w - tw - 4));
      ctx.fillText(label, lx, ly);
    }
    ctx.setLineDash([]);
  }

  private drawCornerLabels(): void {
    const { ctx, w } = this;
    const compact = w < 480;
    ctx.font = `${compact ? 9 : 10}px ${token('font-mono')}`;
    ctx.textBaseline = 'top';
    ctx.fillStyle = this.colDim;
    ctx.textAlign = 'right';
    const label = compact
      ? `${(this.maxFreq / 1000).toFixed(0)}k`
      : `SPAN ${(this.maxFreq / 1000).toFixed(1)} kHz`;
    ctx.fillText(label, w - 6, 4);
    ctx.textAlign = 'left';
  }

  private drawBorder(): void {
    const { ctx, w, h } = this;
    ctx.strokeStyle = this.colBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }
}
